import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import _ from 'lodash'

export const ContentBodyWithMarker = ({
  children,
  enabled = true,
  recordId = '',
  currentZoomLevel = 1
}) => {
  // Get current scroll position from session storage
  useEffect(() => {
    const scrollPosition = sessionStorage.getItem(
      `${recordId}-current-scroll-position`
    )
    if (scrollPosition) {
      setTimeout(() => {
        ref.current.scrollTo(JSON.parse(scrollPosition))
      }, 500)
    }
  }, [recordId])

  const lastFocus = useRef()
  const [moving, setMoving] = useState(false)
  const [marker, setMarker] = useState(null)

  const ref = useRef()

  const handleMousePosition = useMemo(() => {
    const update = _.throttle(
      (xCoord, yCoord) => {
        const rect = ref.current.getBoundingClientRect()

        const x = xCoord - rect.x + ref.current.scrollLeft
        const y = yCoord - rect.y + ref.current.scrollTop

        setMarker({
          x: x,
          y: y,
          height: ref.current.scrollHeight - y
        })
      },
      20,
      {
        leading: true,
        trailing: true
      }
    )

    return e => {
      if (!moving) {
        return
      }

      update(e.pageX, e.pageY)
    }
  }, [moving])

  const handleStart = useCallback(
    e => {
      if (!enabled) {
        return
      }

      lastFocus.current = document.activeElement
      setMoving(true)
      handleMousePosition(e)
    },
    [enabled, handleMousePosition]
  )

  const handleEnd = useCallback(
    e => {
      if (lastFocus.current) {
        lastFocus.current?.focus?.()
        lastFocus.current = undefined
      }
      setMoving(false)
      handleMousePosition(e)
    },
    [handleMousePosition]
  )

  const handleScroll = useCallback(
    e => {
      // Set current attachment window scroll position in session storage
      const scrollTop = e.target.scrollTop
      const scrollLeft = e.target.scrollLeft
      sessionStorage.setItem(
        `${recordId}-current-scroll-position`,
        JSON.stringify({
          top: scrollTop,
          left: scrollLeft
        })
      )
    },
    [recordId]
  )

  return (
    <Container
      onMouseDown={handleStart}
      onMouseMove={handleMousePosition}
      onMouseUp={handleEnd}
      onMouseLeave={handleEnd}
      onScrollCapture={handleScroll}
      ref={ref}
      $hasMarker={marker && enabled}
    >
      {children}

      <Marker
        $visible={marker && enabled}
        style={{
          top: marker?.y ?? -5,
          left: marker?.x ?? -5,
          height: marker?.height ?? 0
        }}
      />
    </Container>
  )
}

ContentBodyWithMarker.propTypes = {
  children: PropTypes.node,
  enabled: PropTypes.bool
}

const Container = styled.div`
  user-select: ${({ $hasMarker }) => ($hasMarker ? 'none' : 'text')};
  overflow: auto;
  width: 100%;
  max-width: 100%;
  height: 100%;
  max-height: 100%;
  position: relative;
`

const Marker = styled.div`
  position: absolute;
  pointer-events: none;

  /* Bottom and Right lines */
  right: 0;
  height: 0;
  border: solid black 2px;
  border-bottom: none;
  border-right: none;

  /* Top and Left lines */
  &:after {
    position: absolute;
    content: ' ';
    bottom: 100%;
    right: 100%;

    width: 2000px;
    height: 2000px;
    border: solid black 2px;
    border-top: none;
    border-left: none;
  }

  /* Central circle */
  &:before {
    width: 8px;
    height: 8px;

    position: absolute;
    content: ' ';
    left: -5px;
    top: -5px;

    border-radius: 100%;
    background-color: black;
  }

  opacity: ${({ $visible }) => ($visible ? 0.5 : 0)};
  transition: 300ms opacity ease-in-out;
`
